/**
 * SuperAdminPortal — clinical console shell for role=superadmin.
 * Owns the full SuperAdmin surface: Overview, Users, Reassign, Activities.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  LayoutGrid, ChevronDown, KeyRound, LogOut,
  Users, UserCog, Activity, Package, Undo2, Clock, ScrollText, Repeat2, Eye,
} from 'lucide-react';
import './styles.css';
import { Toast } from '../admin/ui';
import Avatar from '../components/Avatar';
import OverviewPage from './pages/Overview';
import UsersPage from './pages/Users';
import ReassignPage from './pages/Reassign';
import ActivitiesPage from './pages/Activities';
import InventoryPage from '../admin/pages/Inventory';
import ReturnsPage from '../admin/pages/Returns';
import WaitlistPage from '../admin/pages/Waitlist';
import HistoryPage from '../admin/pages/History';
import ExchangesPage from './pages/Exchanges';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png';

const NAV = [
  { id: 'overview',   label: 'Overview',          icon: LayoutGrid },
  { id: 'inventory',  label: 'Inventory',         icon: Package },
  { id: 'exchanges',  label: 'Exchanges',         icon: Repeat2 },
  { id: 'returns',    label: 'Returns',           icon: Undo2 },
  { id: 'waitlist',   label: 'Waitlist',          icon: Clock },
  { id: 'history',    label: 'Approval History',  icon: ScrollText },
  { id: 'users',      label: 'Users',             icon: Users },
  { id: 'reassign',   label: 'Reassign',          icon: UserCog },
  { id: 'activities', label: 'Activities',        icon: Activity },
];

export default function SuperAdminPortal({ user, onChangePassword, onEditProfile, onLogout, onPreviewRole }) {
  const [page, setPage] = useState('overview');
  const [toast, setToast] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const menuRef = useRef(null);
  const previewRef = useRef(null);

  const showToast = (message, kind = 'ok') => setToast({ id: Date.now(), message, kind });

  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (previewOpen && previewRef.current && !previewRef.current.contains(e.target)) setPreviewOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, previewOpen]);

  const initials = useMemo(() => (user?.full_name || user?.username || '·')
    .split(' ').filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join(''), [user]);

  const handleNav = (item) => {
    setPage(item.id);
  };

  const ctx = { user, showToast, goto: setPage };

  return (
    <div className="admin-portal spa-scope" data-testid="superadmin-portal">
      <header className="ap-topbar">
        <div className="ap-topbar-brand">
          <img className="ap-topbar-logo" src={LOGO_URL} alt="SAID" />
          <div className="ap-topbar-wordmark">
            <strong>SAID <span style={{ fontFamily: 'var(--diis-font-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.24em', color: 'var(--diis-ink-subtle)', marginLeft: 4, verticalAlign: 2 }}>— DIIS</span></strong>
            <span>SuperAdmin Console</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Preview-as switcher — SuperAdmin audits each portal without logging out. */}
          {typeof onPreviewRole === 'function' && (
            <div style={{ position: 'relative' }} ref={previewRef}>
              <button
                type="button"
                className="ap-topbar-btn spa-preview-trigger"
                onClick={() => setPreviewOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={previewOpen}
                data-testid="spa-preview-trigger"
                title="Preview another portal"
              >
                <Eye size={14} />
                <span style={{ marginLeft: 6, fontWeight: 500 }}>Preview as</span>
                <ChevronDown size={13} style={{ marginLeft: 4 }} />
              </button>
              {previewOpen && (
                <div
                  className="ap-user-menu"
                  role="menu"
                  data-testid="spa-preview-menu"
                  style={{ right: 0, left: 'auto' }}
                >
                  <button
                    type="button"
                    onClick={() => { setPreviewOpen(false); onPreviewRole('admin'); }}
                    data-testid="spa-preview-admin"
                  ><UserCog size={15} /> Faculty / Admin</button>
                  <button
                    type="button"
                    onClick={() => { setPreviewOpen(false); onPreviewRole('clerk'); }}
                    data-testid="spa-preview-clerk"
                  ><Package size={15} /> Record Clerk</button>
                  <button
                    type="button"
                    onClick={() => { setPreviewOpen(false); onPreviewRole('student'); }}
                    data-testid="spa-preview-student"
                  ><Users size={15} /> Student</button>
                </div>
              )}
            </div>
          )}

          <div className="ap-topbar-user" ref={menuRef}>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
              <span className="ap-topbar-user-name">{user?.full_name || user?.username}</span>
              <span className="ap-topbar-user-role">SuperAdmin</span>
            </div>
            <button
              type="button"
              className="ap-topbar-btn"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="spa-user-menu-btn"
              aria-label="Account menu"
            >
              <span className="ap-avatar" aria-hidden="true" style={{ padding: 0, overflow: 'hidden' }}>
                <Avatar user={user} size={28} testId="spa-header-avatar" />
              </span>
              <ChevronDown size={14} />
            </button>
            {menuOpen && (
              <div className="ap-user-menu" role="menu" data-testid="spa-user-menu">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onEditProfile && onEditProfile(); }}
                  data-testid="spa-edit-profile-btn"
                ><UserCog size={15} /> Edit profile</button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onChangePassword && onChangePassword(); }}
                  data-testid="spa-change-password-btn"
                ><KeyRound size={15} /> Change password</button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                  data-testid="spa-logout-btn"
                ><LogOut size={15} /> Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="ap-layout">
        <nav className="ap-sidenav" aria-label="Superadmin navigation">
          <div className="ap-nav-group-label">SuperAdmin</div>
          {NAV.map((item) => {
            const active = page === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNav(item)}
                className={`ap-nav-item${active ? ' is-active' : ''}`}
                data-testid={`spa-nav-${item.id}`}
              >
                <item.icon size={17} strokeWidth={1.7} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <main className="ap-main">
          <div className="ap-page">
            {page === 'overview'   && <OverviewPage   ctx={ctx} />}
            {page === 'inventory'  && <InventoryPage  ctx={ctx} />}
            {page === 'exchanges'  && <ExchangesPage  ctx={ctx} />}
            {page === 'returns'    && <ReturnsPage    ctx={ctx} />}
            {page === 'waitlist'   && <WaitlistPage   ctx={ctx} />}
            {page === 'history'    && <HistoryPage    ctx={ctx} />}
            {page === 'users'      && <UsersPage      ctx={ctx} />}
            {page === 'reassign'   && <ReassignPage   ctx={ctx} />}
            {page === 'activities' && <ActivitiesPage ctx={ctx} />}
          </div>
        </main>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
